/**
 * Barevný systém editoru automatizací — JEDINÝ zdroj pravdy. Žádný hex natvrdo
 * v komponentách.
 *
 * Dvě oddělené vrstvy, které se nesmí plést:
 *
 *  A) DATOVÉ TYPY PORTŮ — jediné plně syté prvky na plátně. Barva NENÍ jediný nosič
 *     informace: každý typ má navíc vlastní TVAR a textový POPISEK. (Předtím byly
 *     boolean #f97316 a pulse #f59e0b okem nerozlišitelné, stejně jako string #94a3b8
 *     a any #9ca3af — a byly to právě ty dvojice s odlišnou sémantikou.)
 *
 *  B) KATEGORIE UZLŮ — jen ikonový čip + levý pruh + tečka v paletě. Hlavička uzlu
 *     je neutrální, aby kategorie opticky nesoupeřila s porty.
 *
 * Obojí hlídá `palette.test.ts`: kontrast ≥ 3:1 proti pozadí karty v obou tématech
 * a minimální perceptuální odstup (ΔE76) mezi barvami téže vrstvy. „Ty barvy jsou
 * si moc podobné" je tím pádem chyba testu, ne názor.
 */

import type { DataType, NodeCategory } from "@globals/automations/types";

export type Theme = "light" | "dark";

/** Pozadí karty uzlu — referenční plocha pro kontrast. */
export const CARD_BG: Record<Theme, string> = {
  light: "#ffffff", // --card: oklch(100% 0 0)
  dark: "#26282c", // --card: oklch(20% 0.005 250) v sRGB
};

/** Tvar portu — druhý (nezávislý) nosič informace vedle barvy. */
export type HandleShape = "circle" | "square" | "triangle" | "diamond" | "ring";

export const DATA_TYPE_SHAPE: Record<DataType, HandleShape> = {
  number: "circle",
  boolean: "square",
  pulse: "triangle",
  string: "diamond",
  any: "ring",
};

/**
 * Barvy vybrané MĚŘENÍM, ne od oka: minimální ΔE2000 v rámci vrstvy je
 * 24,1 (light) / 23,4 (dark) — pro srovnání původní kolizní dvojice
 * boolean/pulse měla 15,7 a string/any dokonce 4,0.
 */
export const DATA_TYPE_COLOR: Record<Theme, Record<DataType, string>> = {
  light: {
    number: "#2563eb", // blue-600
    boolean: "#059669", // emerald-600
    pulse: "#e11d48", // rose-600
    string: "#b45309", // amber-700
    any: "#525252", // neutral-600 (bez odstínu — „cokoliv")
  },
  dark: {
    number: "#60a5fa", // blue-400
    boolean: "#34d399", // emerald-400
    pulse: "#fb7185", // rose-400
    string: "#fbbf24", // amber-400
    any: "#a3a3a3", // neutral-400
  },
};

/** Min. ΔE2000 v rámci vrstvy: 24,3 (light) / 22,2 (dark). Žádný hex nesdílí s vrstvou portů. */
export const CATEGORY_COLOR: Record<Theme, Record<NodeCategory, string>> = {
  light: {
    trigger: "#ea580c", // orange-600
    value: "#0284c7", // sky-600
    logic: "#7c3aed", // violet-600
    math: "#0f766e", // teal-700
    time: "#4d7c0f", // lime-700
    flow: "#57534e", // stone-600
    action: "#db2777", // pink-600
  },
  dark: {
    trigger: "#fb923c", // orange-400
    value: "#38bdf8", // sky-400
    logic: "#a78bfa", // violet-400
    math: "#2dd4bf", // teal-400
    time: "#a3e635", // lime-400
    flow: "#a8a29e", // stone-400
    action: "#f472b6", // pink-400
  },
};

export const dataTypeColor = (type: DataType, theme: Theme): string =>
  DATA_TYPE_COLOR[theme][type] ?? DATA_TYPE_COLOR[theme].any;

export const categoryColor = (cat: NodeCategory, theme: Theme): string =>
  CATEGORY_COLOR[theme][cat] ?? CATEGORY_COLOR[theme].value;

// ── Čistá barevná matematika (pro test i případné runtime kontroly) ──────────

type Rgb = { r: number; g: number; b: number };

export const hexToRgb = (hex: string): Rgb => {
  const h = hex.replace("#", "");
  return {
    r: Number.parseInt(h.slice(0, 2), 16),
    g: Number.parseInt(h.slice(2, 4), 16),
    b: Number.parseInt(h.slice(4, 6), 16),
  };
};

const srgbToLinear = (c: number): number => {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** Relativní jas dle WCAG 2.x. */
export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
};

/** Kontrastní poměr dle WCAG 2.x (1–21). Práh pro netextové prvky je 3:1. */
export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** sRGB → CIE L*a*b* (D65), pro perceptuální vzdálenost. */
export const hexToLab = (hex: string): { L: number; a: number; b: number } => {
  const { r, g, b } = hexToRgb(hex);
  const [lr, lg, lb] = [srgbToLinear(r), srgbToLinear(g), srgbToLinear(b)];
  // sRGB → XYZ (D65), normalizováno na bílý bod
  const x = (0.4124 * lr + 0.3576 * lg + 0.1805 * lb) / 0.95047;
  const y = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
  const z = (0.0193 * lr + 0.1192 * lg + 0.9505 * lb) / 1.08883;
  const f = (t: number): number => (t > 216 / 24389 ? Math.cbrt(t) : (841 / 108) * t + 4 / 29);
  const [fx, fy, fz] = [f(x), f(y), f(z)];
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
};

const DEG = Math.PI / 180;

/**
 * Perceptuální vzdálenost CIEDE2000. Záměrně NE prosté CIE76 — to u sytých teplých
 * barev odstup nadhodnocuje (orange-500 vs amber-500 by mu prošly jako „různé",
 * přitom je na 10px kolečku nikdo nerozezná) a v modrofialové oblasti podhodnocuje.
 *
 * Orientačně: <1 = nerozeznatelné, ~2 = práh viditelnosti na velké ploše,
 * >12 = zjevně jiná barva i na malém prvku.
 */
export const deltaE2000 = (hexA: string, hexB: string): number => {
  const p = hexToLab(hexA);
  const q = hexToLab(hexB);

  const avgC = (Math.hypot(p.a, p.b) + Math.hypot(q.a, q.b)) / 2;
  const g = 0.5 * (1 - Math.sqrt(avgC ** 7 / (avgC ** 7 + 25 ** 7)));
  const [ap, aq] = [(1 + g) * p.a, (1 + g) * q.a];
  const [cp, cq] = [Math.hypot(ap, p.b), Math.hypot(aq, q.b)];

  const hue = (a: number, b: number): number => {
    if (a === 0 && b === 0) return 0;
    const h = Math.atan2(b, a) / DEG;
    return h < 0 ? h + 360 : h;
  };
  const [hp, hq] = [hue(ap, p.b), hue(aq, q.b)];

  const dL = q.L - p.L;
  const dC = cq - cp;
  let dh = 0;
  if (cp * cq !== 0) {
    dh = hq - hp;
    if (dh > 180) dh -= 360;
    else if (dh < -180) dh += 360;
  }
  const dH = 2 * Math.sqrt(cp * cq) * Math.sin((dh / 2) * DEG);

  const avgL = (p.L + q.L) / 2;
  const avgCp = (cp + cq) / 2;
  let avgH = hp + hq;
  if (cp * cq !== 0) {
    if (Math.abs(hp - hq) > 180) avgH += hp + hq < 360 ? 360 : -360;
    avgH /= 2;
  } else {
    avgH = hp + hq;
  }

  const t =
    1 -
    0.17 * Math.cos((avgH - 30) * DEG) +
    0.24 * Math.cos(2 * avgH * DEG) +
    0.32 * Math.cos((3 * avgH + 6) * DEG) -
    0.2 * Math.cos((4 * avgH - 63) * DEG);

  const sL = 1 + (0.015 * (avgL - 50) ** 2) / Math.sqrt(20 + (avgL - 50) ** 2);
  const sC = 1 + 0.045 * avgCp;
  const sH = 1 + 0.015 * avgCp * t;
  const rT =
    -2 * Math.sqrt(avgCp ** 7 / (avgCp ** 7 + 25 ** 7)) * Math.sin(60 * Math.exp(-(((avgH - 275) / 25) ** 2)) * DEG);

  return Math.sqrt((dL / sL) ** 2 + (dC / sC) ** 2 + (dH / sH) ** 2 + rT * (dC / sC) * (dH / sH));
};

/** Minimální hodnoty vynucené testem (viz palette.test.ts). */
export const MIN_CONTRAST = 3;
/** Porty nesou informaci hlavně barvou → přísnější práh. */
export const MIN_DELTA_E_DATA_TYPE = 20;
/** Kategorie mají navíc vždy ikonu i textový název → mírnější práh stačí. */
export const MIN_DELTA_E_CATEGORY = 14;
